// Start-page shortcuts: the tile icons, opening one, taking one off the start page (and putting
// it back), the tile menu, and the "All shortcuts" view that always lists every built-in one.
// Visibility is still the scBooks/scTools/... settings flags, so the Settings switches, backups
// and older saves all keep working as they are.
import { SHORTCUTS } from "../shared/settings-schema.js";
import { get, set, setMany } from "../shared/settings-store.js";
import { readJSON, writeJSON } from "../shared/store.js";
import { openFolder } from "./folders.js";
import { openInternal, newTab } from "./tabs.js";
import { openLinux, LINUX_PAGE } from "./linux-open.js";
import { inOS, openDesktop } from "./os-host.js";
import { navigateTab } from "./navigation.js";
import { announce as say } from "./announce.js";
import { builtInShortcuts, shortcutOn, startTiles, addBackPatch, seenPreviews, pinnedTile } from "../shared/shortcuts.js";
import { TOOLS } from "../shared/tools-manifest.js";

const ic = (b) => '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + b + "</svg>";
export const ICONS = {
	books: ic('<rect x="5" y="4" width="14" height="16" rx="1.5"/><line x1="9" y1="4" x2="9" y2="20"/>'),
	worksheets: ic('<rect x="4" y="4" width="6" height="6" rx="1"/><rect x="14" y="4" width="6" height="6" rx="1"/><rect x="4" y="14" width="6" height="6" rx="1"/><rect x="14" y="14" width="6" height="6" rx="1"/>'),
	partners: ic('<path d="M9 14l2 2 4-4"/><path d="M12 3l7 4v5a8 8 0 0 1-7 8 8 8 0 0 1-7-8V7z"/>'),
	leaderboard: ic('<path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0z"/><path d="M17 5h2.5a1.5 1.5 0 0 1 0 5H17M7 5H4.5a1.5 1.5 0 0 0 0 5H7"/>'),
	quests: ic('<path d="M12 3 l2.5 5.6 6.1.5 -4.6 4 1.4 6 -5.4-3.2 -5.4 3.2 1.4-6 -4.6-4 6.1-.5z"/>'),
	forums: ic('<path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 9 9 0 0 1-4-1L3 20l1.5-4.5a8.38 8.38 0 0 1-1-4A8.38 8.38 0 0 1 12 3a8.38 8.38 0 0 1 9 8.5z"/>'),
	tools: ic('<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>'),
	favorites: ic('<path d="M12 3.6l2.5 5.1 5.6.8-4.05 3.95.96 5.55L12 16.9l-5.0 2.65.96-5.55L3.9 9.5l5.6-.8z"/>'),
	history: ic('<circle cx="12" cy="12" r="8"/><polyline points="12,8 12,12 15,14"/><polyline points="4,6 4,10 8,10"/>'),
	help: ic('<circle cx="12" cy="12" r="9"/><path d="M9.2 9.4a2.8 2.8 0 0 1 5.3 1c0 1.8-2.5 2-2.5 3.4"/><line x1="12" y1="17.2" x2="12" y2="17.3"/>'),
	tv: ic('<rect x="3" y="6" width="18" height="12" rx="2"/><polyline points="8,3 12,6 16,3"/>'),
	music: ic('<path d="M9 17V5l11-2v12"/><circle cx="6" cy="17" r="3"/><circle cx="17" cy="15" r="3"/>'),
	// a monitor with two overlapping windows: the desktop environment (Re:Vault OS)
	desktop: ic('<rect x="3" y="4" width="18" height="13" rx="2"/><path d="M9 21h6M12 17v4"/><rect x="6" y="7" width="7" height="5" rx=".8"/><path d="M11 9.5h5.2a.8.8 0 0 1 .8.8V14"/>'),
	// two speech bubbles: Study Hall (live study rooms)
	hall: ic('<path d="M4 5h11a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9l-4 3v-3H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"/><path d="M17 9h3a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-1v3l-4-3h-3"/>'),
	// original line-art penguin (body, bib, beak, eyes, feet) in the same 24px / 1.4 stroke as the rest
	linux: ic('<path d="M12 3.2c-2.5 0-4 2.1-4 4.7 0 1.3.2 2.1-.7 3.5C6.1 13.3 5.2 15 5.2 16.8c0 2.6 2.9 3.9 6.8 3.9s6.8-1.3 6.8-3.9c0-1.8-.9-3.5-2.1-5.4-.9-1.4-.7-2.2-.7-3.5 0-2.6-1.5-4.7-4-4.7z"/><path d="M9.6 11.6c-1 1.3-1.6 2.9-1.6 4.4 0 1.9 1.8 2.9 4 2.9s4-1 4-2.9c0-1.5-.6-3.1-1.6-4.4"/><path d="M10.9 9.4l1.1.8 1.1-.8"/><circle cx="10.5" cy="7.4" r=".35" fill="currentColor"/><circle cx="13.5" cy="7.4" r=".35" fill="currentColor"/><path d="M8.6 20.3l-1.9.6M15.4 20.3l1.9.6"/>'),
	// a 3x3 grid of rounded squares, like an app launcher
	all: ic('<rect x="4" y="4" width="4" height="4" rx="1"/><rect x="10" y="4" width="4" height="4" rx="1"/><rect x="16" y="4" width="4" height="4" rx="1"/><rect x="4" y="10" width="4" height="4" rx="1"/><rect x="10" y="10" width="4" height="4" rx="1"/><rect x="16" y="10" width="4" height="4" rx="1"/><rect x="4" y="16" width="4" height="4" rx="1"/><rect x="10" y="16" width="4" height="4" rx="1"/><rect x="16" y="16" width="4" height="4" rx="1"/>'),
};
const ON_SVG = ic('<circle cx="12" cy="12" r="9"/><polyline points="8,12.5 11,15.5 16,9.5"/>');
const OFF_SVG = ic('<circle cx="12" cy="12" r="9"/><line x1="8" y1="12" x2="16" y2="12"/>');

export const ALL_LABEL = "All shortcuts";
export const ALL_VIEW = "allshortcuts";
const TIP_KEY = "revault:home"; // shared with the start page's own small state (the deck dismissal)

// Remembers preview shortcuts (Study Hall) once they have been on, so they stay listed after removal.
function seen() {
	try {
		const h = Object.assign({}, readJSON(TIP_KEY, {}));
		const was = Array.isArray(h.scSeen) ? h.scSeen : [];
		const now = seenPreviews(SHORTCUTS, get, was);
		if (now.length !== was.length) { h.scSeen = now; writeJSON(TIP_KEY, h); }
		return now;
	} catch (e) { return []; }
}
export const builtIns = () => builtInShortcuts(SHORTCUTS, get, { inOS, seen: seen() });
export const isOnStart = (sc) => shortcutOn(sc, get);
export const tilesOnStart = () => { seen(); const pin = pinnedTile(TOOLS, get); return (pin ? [pin] : []).concat(startTiles(SHORTCUTS, get, { inOS })); };

export function openShortcut(sc) {
	if (sc.env) openDesktop();
	else if (sc.view) openFolder(sc.view);
	else if (sc.page === LINUX_PAGE) openLinux(!!sc.boot);
	else if (sc.page) openInternal(sc.page, sc.label); // openInternal counts a pinned tool like any other tool open
	else if (sc.url) { const id = newTab(); navigateTab(id, sc.url); } // proxied site in a new tab
}
export const openAll = () => { hideToast(); openFolder(ALL_VIEW); };

// --- removing and adding back -------------------------------------------------------------------
export function addBack(sc) {
	setMany(addBackPatch([sc], get));
	say(sc.label + " is back on your start page.");
}

// Taking one off the start page leaves it in All shortcuts. The first time, the toast says so.
export function removeFromStart(sc) {
	set(sc.flag, sc.val ? "" : false);
	let first = false;
	try { const h = Object.assign({}, readJSON(TIP_KEY, {})); first = !h.scTip; if (first) { h.scTip = 1; writeJSON(TIP_KEY, h); } } catch (e) {}
	showRemovedToast(sc, first);
}

let toastEl = null, toastTimer = 0;
// If keyboard focus was in the toast, it goes to the restored tile (Undo) or to All shortcuts.
function hideToast(focusLabel) {
	clearTimeout(toastTimer);
	if (!toastEl) return;
	const had = toastEl.contains(document.activeElement);
	toastEl.remove();
	toastEl = null;
	if (!had) return;
	requestAnimationFrame(() => {
		const tiles = [...document.querySelectorAll("#rv-shortcuts .rv-shortcut-btn")];
		const to = tiles.find((b) => b.getAttribute("aria-label") === focusLabel) || tiles[tiles.length - 1];
		if (to) to.focus();
	});
}
function showRemovedToast(sc, first) {
	hideToast();
	const el = document.createElement("div");
	el.className = "rv-update rv-sctoast";
	const msg = document.createElement("span");
	msg.className = "rv-update-msg";
	const line = sc.label + " removed from your start page.";
	const tip = sc.val ? "You can pin it again from Tools." : "You can always find it in All shortcuts.";
	msg.textContent = line;
	if (first) { const t = document.createElement("span"); t.className = "rv-sctoast-tip"; t.textContent = tip; msg.append(" ", t); }
	const undo = document.createElement("button");
	undo.type = "button"; undo.className = "rv-update-reload"; undo.textContent = "Undo";
	undo.setAttribute("aria-label", "Undo, put " + sc.label + " back");
	undo.addEventListener("click", () => { addBack(sc); hideToast(sc.label); });
	const x = document.createElement("button");
	x.type = "button"; x.className = "rv-update-x"; x.setAttribute("aria-label", "Dismiss"); x.textContent = "✕";
	x.addEventListener("click", () => hideToast());
	el.append(msg, undo, x);
	document.body.appendChild(el);
	toastEl = el;
	requestAnimationFrame(() => el.classList.add("rv-update-in"));
	say(first ? line + " " + tip : line);
	// Stays while the pointer or focus is on it, so there is time to reach Undo.
	const ms = first ? 10000 : 7000;
	const arm = () => { clearTimeout(toastTimer); toastTimer = setTimeout(hideToast, ms); };
	el.addEventListener("pointerenter", () => clearTimeout(toastTimer));
	el.addEventListener("pointerleave", arm);
	el.addEventListener("focusin", () => clearTimeout(toastTimer));
	el.addEventListener("focusout", (e) => { if (!el.contains(e.relatedTarget)) arm(); });
	arm();
}

// --- the tile menu (right-click, long-press, Shift+F10 or the menu key) ---------------------------
let menuEl = null, menuFor = null, openedAt = 0;
function ensureMenu() {
	if (menuEl) return menuEl;
	menuEl = document.createElement("div");
	menuEl.className = "rv-ctxmenu rv-scmenu";
	menuEl.hidden = true;
	menuEl.setAttribute("role", "menu");
	document.body.appendChild(menuEl);
	document.addEventListener("pointerdown", (e) => { if (!menuEl.hidden && !menuEl.contains(e.target)) closeMenu(); }, true);
	document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !menuEl.hidden) { e.preventDefault(); closeMenu(true); } });
	menuEl.addEventListener("keydown", (e) => {
		const items = [...menuEl.querySelectorAll(".rv-ctxitem")];
		const i = items.indexOf(document.activeElement);
		let to = -1;
		if (e.key === "ArrowDown") to = (i + 1) % items.length;
		else if (e.key === "ArrowUp") to = (i - 1 + items.length) % items.length;
		else if (e.key === "Home") to = 0;
		else if (e.key === "End") to = items.length - 1;
		else if (e.key === "Tab") { e.preventDefault(); closeMenu(true); return; }
		if (to < 0) return;
		e.preventDefault();
		items[to].focus();
	});
	window.addEventListener("blur", () => closeMenu());
	window.addEventListener("resize", () => closeMenu());
	return menuEl;
}
function closeMenu(restore) {
	if (!menuEl || menuEl.hidden) return;
	const inside = menuEl.contains(document.activeElement);
	menuEl.hidden = true;
	const back = menuFor;
	menuFor = null;
	if ((restore || inside) && back && back.isConnected) back.focus();
}

export function openTileMenu(sc, btn, x, y, fromKeyboard) {
	// A long-press can also fire a native contextmenu right after; one menu is enough.
	if (menuEl && !menuEl.hidden && menuFor === btn && Date.now() - openedAt < 600) return;
	hideToast();
	const m = ensureMenu();
	m.textContent = "";
	m.setAttribute("aria-label", sc.label);
	menuFor = btn;
	openedAt = Date.now();
	const add = (label, fn) => {
		const b = document.createElement("button");
		b.className = "rv-ctxitem"; b.type = "button"; b.textContent = label; b.setAttribute("role", "menuitem"); b.tabIndex = -1;
		b.addEventListener("click", () => { const kb = m.contains(document.activeElement); menuFor = null; m.hidden = true; fn(kb); });
		m.appendChild(b);
	};
	add("Open", () => openShortcut(sc));
	add("Remove from start page", (kb) => removeTile(sc, btn, kb));
	const sep = document.createElement("div"); sep.className = "rv-ctxsep"; sep.setAttribute("role", "separator"); m.appendChild(sep);
	add(ALL_LABEL, () => openAll());
	m.hidden = false;
	const w = m.offsetWidth || 200, h = m.offsetHeight || 140;
	m.style.left = Math.max(4, Math.min(x, window.innerWidth - w - 6)) + "px";
	m.style.top = Math.max(4, Math.min(y, window.innerHeight - h - 6)) + "px";
	if (fromKeyboard) { const first = m.querySelector(".rv-ctxitem"); if (first) first.focus(); }
}

// Removes a tile from the start page; keyboard focus moves to the tile that takes its place.
export function removeTile(sc, btn, keepFocus) {
	const row = btn && btn.closest(".rv-shortcuts");
	const tiles = row ? [...row.querySelectorAll(".rv-shortcut-btn")] : [];
	const at = tiles.indexOf(btn);
	removeFromStart(sc);
	if (!keepFocus || !row) return;
	requestAnimationFrame(() => {
		const now = [...row.querySelectorAll(".rv-shortcut-btn")];
		const next = now[Math.min(Math.max(at, 0), now.length - 1)];
		if (next) next.focus();
	});
}

// Touch and pen long-press opens the menu. Pointer events, not touch events, so a convertible
// Chromebook in laptop mode (which reports a fine pointer) still gets it from the screen.
let pressedAt = 0;
export function attachTileMenu(btn, sc) {
	let timer = 0, sx = 0, sy = 0;
	const cancel = () => { clearTimeout(timer); timer = 0; };
	btn.addEventListener("pointerdown", (e) => {
		if (e.pointerType === "mouse") return;
		sx = e.clientX; sy = e.clientY;
		cancel();
		timer = setTimeout(() => { timer = 0; pressedAt = Date.now(); openTileMenu(sc, btn, sx, sy); }, 500);
	});
	btn.addEventListener("pointermove", (e) => { if (timer && (Math.abs(e.clientX - sx) > 10 || Math.abs(e.clientY - sy) > 10)) cancel(); });
	btn.addEventListener("pointerup", cancel);
	btn.addEventListener("pointercancel", cancel);
	btn.addEventListener("pointerleave", cancel);
	btn.addEventListener("contextmenu", (e) => {
		e.preventDefault();
		// The menu key and Shift+F10 fire this with no pointer position: open it under the tile.
		if (!e.clientX && !e.clientY) { const r = btn.getBoundingClientRect(); openTileMenu(sc, btn, r.left, r.bottom + 4, true); }
		else openTileMenu(sc, btn, e.clientX, e.clientY);
	});
	btn.addEventListener("keydown", (e) => {
		if ((e.key === "F10" && e.shiftKey) || e.key === "ContextMenu") {
			e.preventDefault();
			const r = btn.getBoundingClientRect();
			openTileMenu(sc, btn, r.left, r.bottom + 4, true);
		} else if (e.key === "Delete") {
			e.preventDefault();
			removeTile(sc, btn, true);
		}
	});
}
// The click that ends a long-press should not also open the shortcut.
export const justLongPressed = () => Date.now() - pressedAt < 800;

// --- the All shortcuts view ------------------------------------------------------------------------
function el(tag, cls, text) { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
function actionBtn(cls, text, name, fn) {
	const b = el("button", "rv-allsc-btn " + cls, text);
	b.type = "button";
	b.setAttribute("aria-label", text + ": " + name); // the visible words come first (label in name)
	b.addEventListener("click", fn);
	return b;
}

export function renderAllShortcuts(root) {
	const keep = document.activeElement && root.contains(document.activeElement) ? document.activeElement.dataset.focus : null;
	root.textContent = "";
	const wrap = el("div", "rv-allsc");
	const list = builtIns();
	const hidden = list.filter((sc) => !isOnStart(sc));
	const rowOff = get("showShortcuts") === false;

	wrap.append(el("p", "rv-allsc-intro", "Every shortcut lives here, even the ones you take off your start page."));
	if (rowOff) {
		const note = el("div", "rv-allsc-note");
		note.append(el("span", "", "Shortcuts are turned off on your start page, so only All shortcuts shows there."));
		const on = el("button", "rv-allsc-btn rv-allsc-add", "Show shortcuts");
		on.type = "button"; on.dataset.focus = "row-on";
		on.addEventListener("click", () => { set("showShortcuts", true); say("Shortcuts are back on your start page."); });
		note.append(on);
		wrap.append(note);
	}
	const sum = el("p", "rv-allsc-sum");
	sum.textContent = (list.length - hidden.length) + (rowOff ? " turned on, " : " on your start page, ") + hidden.length + " hidden";
	wrap.append(sum);
	if (hidden.length > 1) {
		const all = el("button", "rv-allsc-btn rv-allsc-add rv-allsc-addall", "Add all back");
		all.type = "button"; all.dataset.focus = "addall";
		all.addEventListener("click", () => {
			setMany(addBackPatch(hidden, get));
			say("All shortcuts are back on your start page.");
		});
		wrap.append(all);
	}

	const ul = el("ul", "rv-allsc-list");
	ul.setAttribute("aria-label", ALL_LABEL);
	for (const sc of list) {
		const on = isOnStart(sc);
		const li = el("li", "rv-allsc-item" + (on ? "" : " off"));
		const icon = el("span", "rv-allsc-icon");
		icon.innerHTML = ICONS[sc.id] || "";
		const text = el("span", "rv-allsc-text");
		const name = el("span", "rv-allsc-name", sc.label);
		const state = el("span", "rv-allsc-state");
		state.innerHTML = on ? ON_SVG : OFF_SVG;
		state.append(el("span", "", !on ? "Hidden" : rowOff ? "Turned on" : "On your start page"));
		text.append(name, state);
		const acts = el("span", "rv-allsc-acts");
		const open = actionBtn("", "Open", sc.label, () => openShortcut(sc));
		open.dataset.focus = "open-" + sc.id;
		const flip = on
			? actionBtn("rv-allsc-remove", "Remove from start page", sc.label, () => { set(sc.flag, false); say(sc.label + " is hidden. It stays here."); })
			: actionBtn("rv-allsc-add", "Add back to start page", sc.label, () => addBack(sc));
		flip.dataset.focus = "flip-" + sc.id;
		acts.append(open, flip);
		li.append(icon, text, acts);
		ul.append(li);
	}
	wrap.append(ul);
	root.append(wrap);
	if (keep) {
		const target = root.querySelector('[data-focus="' + keep + '"]') || (keep === "addall" || keep === "row-on" ? root.querySelector(".rv-allsc-list button") : null);
		if (target) target.focus({ preventScroll: true });
	}
}
